import { useCallback, useSyncExternalStore } from 'react';

// A boolean kept per-browser in localStorage under `storageKey`, exposed as a hook. Every store
// access is guarded -- a private window or a blocked store throws on read/write -- and the flag
// degrades to `false` rather than erroring. State lives in a module-level store per key so every
// consumer sees the same value without a reload (matching `useFavouriteTemplates`).
//
// The flag records the user's *intent*; whether it takes effect can depend on other conditions
// (e.g. viewport width) -- that gate lives in the consumer.
export function createPersistentFlag(storageKey: string): () => readonly [boolean, (value: boolean) => void] {
  function read(): boolean {
    try {
      return globalThis.localStorage?.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  }

  function write(value: boolean): void {
    try {
      globalThis.localStorage?.setItem(storageKey, value ? '1' : '0');
    } catch {
      // Best effort: the in-memory value still drives this session.
    }
  }

  let current = read();
  const listeners = new Set<() => void>();

  function commit(next: boolean): void {
    if (next === current) return;
    current = next;
    write(next);
    for (const listener of listeners) listener();
  }

  return function usePersistentFlag() {
    const value = useSyncExternalStore(
      (listener) => {
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
      () => current,
      () => false
    );
    const setValue = useCallback((next: boolean) => commit(next), []);
    return [value, setValue] as const;
  };
}
